<template>
  <div class="container-fluid">
    <div class="row starter-main mt-5 pt-5">
      <TableTitle
        :title="title"
        :pageTitle="pageTitle"
        icon="fas fa-road"
        :showAction="true"
        actionLabel="Create New"
        modal="createNew"
        :disabled="!canCreate('data')"
      />
      <CommonTable2
        :tableObj="tableConfig"
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
        permissionKey="data"
      />
    </div>
  </div>

  <!-- pagination -->
  <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4">
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted">Rows per page:</span>
      <select
        class="form-select form-select-sm rows-select"
        v-model="tablePagination.per_page"
        @change="changeRows"
      >
        <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
          {{ n }}
        </option>
      </select>
    </div>
    <div class="pagination-mini">
      <button
        class="page-btn prev_btn"
        :disabled="!tablePagination.has_prev"
        @click="handlePageChange(tablePagination.current_page - 1)"
      >
        «
      </button>
      <button
        v-for="(p, index) in visiblePages"
        :key="index"
        class="page-btn"
        :class="{
          'page-active': p === tablePagination.current_page,
          'disabled-dots': p === '...',
        }"
        :disabled="p === '...'"
        @click="p !== '...' && typeof p === 'number' && handlePageChange(p)"
      >
        {{ p }}
      </button>
      <button
        class="page-btn next_btn"
        :disabled="!tablePagination.has_next"
        @click="handlePageChange(tablePagination.current_page + 1)"
      >
        »
      </button>
    </div>
  </div>

  <!-- Create Modal -->
  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    :save-button-disabled="isCreateFormValid"
  >

    <template #body>
      <BaseForm
        :fields="formFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
        ref="createFormRef"
      />
    </template>
  </BaseModal>

  <!-- Edit/View Modal -->
  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormValid"
    :loading="isSubmitting"
  >
    <template #body>
      <BaseForm
        :fields="formFieldsWithReadonly"
        v-model="editingItemData"
        @submit="handleSaveEdit"
        ref="editFormRef"
      />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm from "@/components/commen/modal/BaseForm.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import { usePermissions } from "@/composables/usePermissions";
import apiService from "@/services/apiService";
import { toast } from "vue3-toastify";

const { canCreate } = usePermissions();

const API_RESOURCE = "/common/workspace-descriptions";
const FILTER_RESOURCE = "/common/workspace-descriptions-filter";
const category = "lane";
const pageTitle = "Lane Suggestion";
const title = "Lane Suggestions List";

const crud = useCrud(API_RESOURCE);
const isSubmitting = ref(false);
const isUploading = ref(false);

const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;
  if (!total || total <= 4) return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  const pages: (number | string)[] = [];
  pages.push(1);
  if (current > 4) pages.push("...");
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
  if (current < total - 3) pages.push("...");
  pages.push(total);
  return pages;
});
 

const tableConfig = ref({
  columns: [
    { name: "Image", field: "featured_image", width: "100px" },
    { name: "Title", field: "title", sortable: true, width: "200px" },
    { name: "Description", field: "description", sortable: true, width: "auto" },
  ],
  data: [] as any[],
  actions: [true],
  loading: crud.isLoading,
  table_width: "100%",
});

// Custom fetch to handle the filter endpoint
const fetchFilteredData = async (params = { page: 1, limit: 10 }) => {
  crud.isLoading.value = true;
  try {
    const res = await apiService.get(FILTER_RESOURCE, {
      ...params,
      description_category: category,
    });
    // The filter endpoint response structure might differ, ensure it maps to what CommonTable2 expects
    if (res && res.data) {
       // standardizeResponse in apiService usually handles this, 
       // but useCrud's data is what we want to sync with
       crud.data.value = res.data;
       crud.pagination.value = res.pagination || {};
    } else if (Array.isArray(res)) {
       crud.data.value = res;
    }
  } catch (error) {
    console.error("Error fetching filtered lane suggestions:", error);
  } finally {
    crud.isLoading.value = false;
  }
};

watch(crud.data, (newVal) => {
  tableConfig.value.data = newVal.map(item => ({
    ...item,
    featured_image: item.file?.url // Map file.url to featured_image for CommonTable2 display
  }));
});

watch(crud.pagination, (newVal: any) => {
  if (!newVal) return;
  tablePagination.value.current_page = newVal.current_page || 1;
  tablePagination.value.total_pages = newVal.total_pages || 1;
  tablePagination.value.total = newVal.total || 0;
  tablePagination.value.has_prev = tablePagination.value.current_page > 1;
  tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
}, { deep: true });

const changeRows = () => {
  currentPage.value = 1;
  fetchFilteredData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  currentPage.value = page;
  fetchFilteredData({ page, limit: tablePagination.value.per_page });
};

const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

const newItemData = ref<any>({ title: "", description: "", image: null, url: "", name: "" });
const editingItemData = ref<any>({ _id: "", title: "", description: "", image: null, url: "", name: "" });

const formFields = [
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter title" },
  { key: "description", label: "Description", type: "textarea", required: true, placeholder: "Enter description" },
  { key: "image", label: "Image", type: "file", required: false, accept: "image/*" },
];

const formFieldsWithReadonly = computed(() => formFields.map(field => ({
  ...field,
  readonly: isViewMode.value
})));

const isCreateFormValid = computed(() => !newItemData.value.title?.trim() || !newItemData.value.description?.trim() || isUploading.value);
const isEditFormValid = computed(() => !editingItemData.value.title?.trim() || !editingItemData.value.description?.trim() || isUploading.value);

const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { 
    ...row, 
    image: row.file?.url || "",
    url: row.file?.url || "",
    name: row.file?.name || ""
  };
  isEditModalOpen.value = true;
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { 
    ...row,
    image: row.file?.url || ""
  };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this lane suggestion?")) {
    await crud.deleteItem(id);
    fetchFilteredData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
};

const uploadImage = async (file: File) => {
  isUploading.value = true;
  try {
    // Passing "upload/file" without leading slash to ensure it uses the baseURL properly
    const res = await apiService.uploadFile("upload/file-common", file);
    if (res && res.url) {
      toast.success("Image uploaded successfully");
      return { url: res.url, name: res.name || file.name };
    }
    toast.error("Image upload returned invalid data");
    return null;
  } catch (error) {
    console.error("Upload error:", error);
    toast.error("Image upload failed");
    return null;
  } finally {
    isUploading.value = false;
  }
};

// Watchers for immediate upload
watch(() => newItemData.value.image, async (newFile) => {
  if (newFile instanceof File) {
    const imageData = await uploadImage(newFile);
    if (imageData) {
      newItemData.value.url = imageData.url;
      newItemData.value.name = imageData.name;
    }
  }
});

watch(() => editingItemData.value.image, async (newFile) => {
  if (newFile instanceof File) {
    const imageData = await uploadImage(newFile);
    if (imageData) {
      editingItemData.value.url = imageData.url;
      editingItemData.value.name = imageData.name;
    }
  }
});

const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;
  isSubmitting.value = true;
  try {
    const payload = {
      title: newItemData.value.title,
      description: newItemData.value.description,
      description_category: category,
      file:{
          name: newItemData.value.name,
          url: newItemData.value.url,
      }
    };

    const result = await crud.createItem(payload);
    if (result) {
      closeCreateModal();
      fetchFilteredData({ page: 1, limit: tablePagination.value.per_page });
    }
  } catch (error) {
    console.error("Error creating lane suggestion:", error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleSaveEdit = async () => {
  if (isEditFormValid.value) return;
  const id = editingItemData.value._id;
  if (!id) return;
  isSubmitting.value = true;
  try {
    const payload = {
      title: editingItemData.value.title,
      description: editingItemData.value.description,
      file:{
          name: editingItemData.value.name,
          url: editingItemData.value.url,
      }
    };

    const result = await crud.updateItem(id, payload);
    if (result) {
      closeEditModal();
      fetchFilteredData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    }
  } catch (error) {
    console.error("Error updating lane suggestion:", error);
  } finally {
    isSubmitting.value = false;
  }
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  newItemData.value = { title: "", description: "", image: null, url: "", name: "" };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = { _id: "", title: "", description: "", image: null, url: "", name: "" };
};


onMounted(() => {
  fetchFilteredData({ page: 1, limit: tablePagination.value.per_page });
});
</script>

<style scoped>
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}
.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}
.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child { border-top-left-radius: 6px; border-bottom-left-radius: 6px; }
.page-btn:last-child { border-top-right-radius: 6px; border-bottom-right-radius: 6px; }
.page-btn:hover { background: #e9ecef; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-active {
  background: #21c1d6;
  color: #fff;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  border: 1px solid #21c1d6;
}
.prev_btn, .next_btn { color: #21c1d6; }
</style>
